import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import dayjs from 'dayjs';
import React, { useEffect, useMemo } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  useDeleteApiTripReportsByIdMutation,
  useGetApiTripReportsByIdQuery,
  useGetApiUsersMeQuery,
  usePostApiTripReportsByIdSubmitMutation,
} from '../../app/athenaApiGen';
import DynamicForm from '../../components/dynamic-form/DynamicForm';
import DeleteConfirmation from '../../components/Modals/DeleteConfirmation';
import {
  createFormDataChangeHandler,
  parseRawFormData,
} from '../../dashboard/components/formUtils';
import useSchemaForm from '../../hooks/useSchemaForm';
import { trFormSchema } from './TRFormSchema';
import { readLocationId } from './TRUtils';

const DEBUG_MODE = false; // Toggle this for debug features (unused when using DynamicForm submit)

export default function TRForm() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: meData } = useGetApiUsersMeQuery();
  const [formData, setFormData, isLoading, resetFormData] =
    useSchemaForm('TRFormSchema');
  const [initialData, setInitialData] = React.useState(null);
  const { data: trResp, isFetching: isFetchingTR } =
    useGetApiTripReportsByIdQuery({ id: id || '' }, { skip: !id });
  const [deleteTripReport, { isLoading: deleting }] =
    useDeleteApiTripReportsByIdMutation();
  const [submitTripReport] = usePostApiTripReportsByIdSubmitMutation();
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const isCloneMode = searchParams.get('mode') === 'clone';
  const [savedTrId, setSavedTrId] = React.useState(isCloneMode ? null : (id || null)); // Store ID after save

  // Always start fresh on no-id route navigations like /tr
  useEffect(() => {
    if (id) return;
    resetFormData();
    setSavedTrId(null);
    setInitialData(null);
    setDeleteDialogOpen(false);
    setSnackbar({ open: false, message: '', severity: 'success' });
  }, [id, location.key, resetFormData]);

  const parsedRaw = useMemo(() => {
    const raw = trResp?.rawFormData || trResp?.RawFormData;
    return raw ? parseRawFormData(raw) : null;
  }, [trResp]);

  // Load by id: prefill from rawFormData when available
  useEffect(() => {
    if (!id || !trResp) return;

    const mergedSource = parsedRaw
      ? { ...trResp, ...parsedRaw }
      : { ...trResp };
    const normalizeDate = (value) => (value ? dayjs(value) : null);
    const pickLocationId = (...candidates) => {
      for (const candidate of candidates) {
        const resolved = readLocationId(candidate);
        if (resolved) return resolved;
      }
      return '';
    };

    const travelStart =
      parsedRaw?.travelDates?.start ??
      parsedRaw?.fromDate ??
      trResp?.fromDate ??
      mergedSource.travelDates?.start ??
      null;
    const travelEnd =
      parsedRaw?.travelDates?.end ??
      parsedRaw?.toDate ??
      trResp?.toDate ??
      mergedSource.travelDates?.end ??
      null;

    const fromLocationId = pickLocationId(
      parsedRaw?.fromLocationOfTravel,
      trResp?.fromLocationOfTravel,
      mergedSource.fromLocationOfTravel,
    );
    const toLocationId = pickLocationId(
      parsedRaw?.toLocationOfTravel,
      trResp?.toLocationOfTravel,
      mergedSource.toLocationOfTravel,
    );

    const normalized = {
      ...mergedSource,
      trDate: mergedSource.trDate ? normalizeDate(mergedSource.trDate) : null,
      dueDate: mergedSource.dueDate ? normalizeDate(mergedSource.dueDate) : null,
      // TAR-style: UI uses contactId fields; backend persists traveler ids
      contactId:
        mergedSource.contactId ??
        mergedSource.travelerId ??
        parsedRaw?.contactId ??
        parsedRaw?.travelerId ??
        '',
      pocContactId:
        mergedSource.pocContactId ??
        mergedSource.pocTravelerId ??
        parsedRaw?.pocContactId ??
        parsedRaw?.pocTravelerId ??
        '',
      travelDates: {
        start: travelStart ? normalizeDate(travelStart) : null,
        end: travelEnd ? normalizeDate(travelEnd) : null,
      },
      fromLocationOfTravel: fromLocationId,
      toLocationOfTravel: toLocationId,
      totalCostOfTrip:
        mergedSource.totalCostOfTrip ?? mergedSource.totalCost ?? '',
      // Ensure workflowState is preserved for delete button logic (clear for clones)
      workflowState: isCloneMode ? '' : trResp?.workflowState,
    };

    setFormData((prev) => ({ ...prev, ...normalized }));

    // Store initial data for attachment change tracking (only set once)
    if (!initialData) {
      setInitialData(normalized);
    }
  }, [id, isCloneMode, parsedRaw, trResp, setFormData, initialData]);

  // Auto-calculate durationDays from travelDates (inclusive of start/end)
  useEffect(() => {
    const start = formData?.travelDates?.start;
    const end = formData?.travelDates?.end;
    let nextVal = '';
    if (start && end) {
      const s = dayjs(start).startOf('day');
      const e = dayjs(end).startOf('day');
      if (s.isValid() && e.isValid() && (e.isAfter(s) || e.isSame(s))) {
        const diffDays = e.diff(s, 'day') + 1; // inclusive
        nextVal = String(diffDays);
      }
    }
    if ((formData?.durationDays ?? '') !== nextVal) {
      setFormData((prev) => ({ ...prev, durationDays: nextVal }));
    }
  }, [
    formData?.travelDates?.start,
    formData?.travelDates?.end,
    formData?.durationDays,
    setFormData,
  ]);

  if (isLoading || (id && isFetchingTR && !parsedRaw)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Universal setter using shared utility
  const handleFormDataChange = createFormDataChangeHandler(setFormData);

  // Default to view mode when editing an existing record (id present),
  // unless explicitly set to edit via ?mode=edit.
  // Also force view mode if workflow has progressed past Pending.
  const editableWorkflow = !formData?.workflowState || formData.workflowState === 'Pending';
  const isViewMode = isCloneMode ? false : (id ? searchParams.get('mode') !== 'edit' || !editableWorkflow : false);

  // Handle cancel — act like the browser back button so callers from the
  // approval queue or elsewhere land back on the page they came from
  // instead of being forced to the TR list.
  const handleCancel = () => {
    navigate(-1);
  };

  // Handle successful submission - navigate back to list
  const handleSubmitSuccess = (result) => {
    if (result.ok && result.payload) {
      // Store the returned ID for subsequent saves
      if (result.payload.trId) {
        setSavedTrId(result.payload.trId);
      }
    }
    if (result.ok && !result.skipNavigation) {
      setTimeout(() => navigate('/tr-list'), 2500);
    }
  };

  // Handle delete
  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteTripReport({ id: id }).unwrap();
      setDeleteDialogOpen(false);
      setSnackbar({
        open: true,
        message: 'Trip Report deleted successfully',
        severity: 'success',
      });
      setTimeout(() => navigate('/tr-list'), 2500);
    } catch (error) {
      console.error('Failed to delete Trip Report', error);
      setDeleteDialogOpen(false);
      setSnackbar({
        open: true,
        message: 'Failed to delete Trip Report. Please try again.',
        severity: 'error',
      });
    }
  };

  const canDelete = id && editableWorkflow && formData?.workflowState !== undefined;

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '100%' } }}>
      <DynamicForm
        schema={trFormSchema}
        formData={formData}
        setFormData={handleFormDataChange}
        onSubmit={handleSubmitSuccess}
        showSubmit={isCloneMode || !savedTrId}
        isEditMode={!isCloneMode && Boolean(savedTrId)}
        recordId={isCloneMode ? null : savedTrId}
        readOnly={isViewMode}
        onCancel={handleCancel}
        onDelete={canDelete ? handleDeleteClick : undefined}
        submitMutation={submitTripReport}
        initialData={initialData}
        authorId={meData?.contact?.contactId}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmation
        open={deleteDialogOpen}
        title="Delete Trip Report"
        onClose={() => setDeleteDialogOpen(false)}
        content={{
          mainText: `
                                    Are you sure you want to delete Trip Report <strong>${formData?.trNumber}</strong>?
                                    <br /><br />
                                    This action cannot be undone.`,
        }}
        handleConfirmation={handleDeleteConfirm}
        isLoading={deleting}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
